import { useMemo, useState } from "react";
import { usePersistentState } from "../../../hooks/usePersistentState";
import BackButton from "../../../components/navigation/BackButton";
import HeaderFilterCell, {
  FilterFooter,
  FilterOptionList,
  FilterRangeInputs,
  FilterSearchInput,
} from "../../../components/table/HeaderFilterCell";
import { previewBankTransferImportRequest } from "../../../lib/api";
import {
  isBankTransferActive,
  normalizeReceiptNo,
  type BankTransferRecord,
} from "../../../lib/bankTransferStore";
import { useBankTransfers } from "../../../hooks/useBankTransfers";
import { matchesContains, matchesNumericRange } from "../../../lib/tableFilters";
import { matchesOptionalDateRange } from "../../../lib/dateFilters";

const HAVALE_TEMPLATE_URL = new URL("../../../assets/havale-ornek-sablon.xlsx", import.meta.url).href;

type SortKey = "yuklemeNo" | "createdAt" | "tarih" | "fisNo" | "aciklama" | "tutar" | "durum";
type SortDir = "asc" | "desc";

interface Filters {
  yuklemeNo: string;
  dateFrom: string;
  dateTo: string;
  fisNo: string;
  aciklama: string;
  tutar: { min: string; max: string };
  durum: string[];
}

const EMPTY_FILTERS: Filters = {
  yuklemeNo: "",
  dateFrom: "",
  dateTo: "",
  fisNo: "",
  aciklama: "",
  tutar: { min: "", max: "" },
  durum: [],
};

function toWholeTl(value: unknown) {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? Math.round(numeric) : 0;
}

function fmtMoney(value: number) {
  return toWholeTl(value).toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

function formatDate(value?: string) {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function toggleSelection(list: string[], value: string) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function getTransferStatus(item: BankTransferRecord) {
  if (item.automaticFilled) return "Otomatik Dolduruldu";
  if (item.manualFilled) return "Manuel Dolduruldu";
  return "Aktif";
}

function blankTransfer(nextNo = 0): BankTransferRecord {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    yuklemeNo: nextNo,
    tarih: now.slice(0, 10),
    fisNo: "",
    aciklama: "",
    tutar: 0,
    automaticFilled: false,
    manualFilled: false,
    manualNote: "",
    createdAt: now,
    updatedAt: now,
  };
}

function compareText(a: string, b: string) {
  return a.localeCompare(b, "tr", { numeric: true, sensitivity: "base" });
}

function getYuklemeNo(item: BankTransferRecord) {
  return Number(item.yuklemeNo || 0);
}

function maxYuklemeNo(list: BankTransferRecord[]) {
  return list.reduce((max, item) => Math.max(max, getYuklemeNo(item)), 0);
}

export default function HavalePage() {
  const { transfers, setAllBankTransfers } = useBankTransfers();
  const [filters, setFilters] = usePersistentState<Filters>("ec.filters.havale.v3", EMPTY_FILTERS);
  const [sortKey, setSortKey] = usePersistentState<SortKey>("ec.sort.havale.key.v2", "yuklemeNo");
  const [sortDir, setSortDir] = usePersistentState<SortDir>("ec.sort.havale.dir.v1", "desc");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<BankTransferRecord | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    return [...transfers]
      .filter((item) => {
        if (filters.yuklemeNo.trim() && !matchesContains(getYuklemeNo(item), filters.yuklemeNo)) return false;
        if (!matchesOptionalDateRange(item.tarih, filters.dateFrom, filters.dateTo)) return false;
        if (!matchesContains(item.fisNo, filters.fisNo)) return false;
        if (!matchesContains(item.aciklama, filters.aciklama)) return false;
        if (!matchesNumericRange(item.tutar, filters.tutar)) return false;
        if (filters.durum.length > 0 && !filters.durum.includes(getTransferStatus(item))) return false;
        return true;
      })
      .sort((a, b) => {
        let result = 0;
        if (sortKey === "yuklemeNo") result = getYuklemeNo(a) - getYuklemeNo(b);
        else if (sortKey === "tutar") result = toWholeTl(a.tutar) - toWholeTl(b.tutar);
        else if (sortKey === "tarih") result = compareText(String(a.tarih || ""), String(b.tarih || ""));
        else if (sortKey === "fisNo") result = compareText(String(a.fisNo || ""), String(b.fisNo || ""));
        else if (sortKey === "aciklama") result = compareText(String(a.aciklama || ""), String(b.aciklama || ""));
        else if (sortKey === "durum") result = compareText(getTransferStatus(a), getTransferStatus(b));
        else result = compareText(String(a.createdAt || ""), String(b.createdAt || ""));
        return sortDir === "asc" ? result : -result;
      });
  }, [filters, sortDir, sortKey, transfers]);

  const summary = useMemo(() => {
    const active = transfers.filter(isBankTransferActive);
    return {
      total: transfers.length,
      active: active.length,
      passive: transfers.length - active.length,
      activeAmount: active.reduce((sum, item) => sum + toWholeTl(item.tutar), 0),
    };
  }, [transfers]);

  const hasActiveFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  function setSort(key: SortKey, dir: SortDir) {
    setSortKey(key);
    setSortDir(dir);
  }

  function renderSortControls(key: SortKey, ascLabel = "A-Z / Artan", descLabel = "Z-A / Azalan") {
    return (
      <div className="mb-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setSort(key, "asc")}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${sortKey === key && sortDir === "asc" ? "border-farm-earth bg-farm-earth-pale text-farm-brown" : "border-farm-border bg-farm-cream text-farm-muted hover:text-farm-brown"}`}
        >
          {ascLabel}
        </button>
        <button
          type="button"
          onClick={() => setSort(key, "desc")}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${sortKey === key && sortDir === "desc" ? "border-farm-earth bg-farm-earth-pale text-farm-brown" : "border-farm-border bg-farm-cream text-farm-muted hover:text-farm-brown"}`}
        >
          {descLabel}
        </button>
      </div>
    );
  }

  async function handleFileUpload(file?: File | null) {
    if (!file) return;
    setError("");
    setMessage("");
    setLoading(true);

    try {
      const preview = await previewBankTransferImportRequest(file);
      const now = new Date().toISOString();
      let added = 0;
      let duplicate = 0;
      let invalid = 0;

      await setAllBankTransfers((prev) => {
        const receiptSet = new Set(prev.map((item) => normalizeReceiptNo(item.fisNo)));
        const next = [...prev];
        let nextYuklemeNo = maxYuklemeNo(prev);

        for (const row of preview.rows) {
          if (row.issues.length > 0) {
            invalid++;
            continue;
          }

          const key = normalizeReceiptNo(row.fis_no);
          if (!key || receiptSet.has(key)) {
            duplicate++;
            continue;
          }

          receiptSet.add(key);
          next.unshift({
            id: crypto.randomUUID(),
            yuklemeNo: ++nextYuklemeNo,
            tarih: row.tarih,
            fisNo: row.fis_no.trim(),
            aciklama: row.aciklama.trim(),
            tutar: toWholeTl(row.tutar),
            automaticFilled: false,
            manualFilled: false,
            manualNote: "",
            createdAt: now,
            updatedAt: now,
          });
          added++;
        }

        return next;
      });

      setMessage(`${added} yeni havale eklendi • ${duplicate} mükerrer atlandı • ${invalid} hatalı satır atlandı`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Havale dosyası okunamadı.");
    } finally {
      setLoading(false);
    }
  }

  async function saveEditing() {
    if (!editing) return;
    if (!editing.fisNo.trim()) {
      setError("Fiş No boş olamaz.");
      return;
    }

    const duplicate = transfers.some(
      (item) => item.id !== editing.id && normalizeReceiptNo(item.fisNo) === normalizeReceiptNo(editing.fisNo)
    );

    if (duplicate) {
      setError("Bu Fiş No zaten kayıtlı. Aynı fiş ikinci kez eklenemez.");
      return;
    }

    const updated = { ...editing, yuklemeNo: editing.yuklemeNo || maxYuklemeNo(transfers) + 1, tutar: toWholeTl(editing.tutar), updatedAt: new Date().toISOString() };
    await setAllBankTransfers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    setEditing(null);
    setError("");
  }

  async function deleteTransfer(id: string) {
    const ok = window.confirm("Bu havale kaydı silinsin mi?");
    if (!ok) return;
    await setAllBankTransfers((prev) => prev.filter((item) => item.id !== id));
  }

  async function toggleManual(item: BankTransferRecord) {
    await setAllBankTransfers((prev) =>
      prev.map((record) =>
        record.id === item.id
          ? { ...record, manualFilled: !record.manualFilled, updatedAt: new Date().toISOString() }
          : record
      )
    );
  }

  function renderFilterPanel() {
    return (
      <div className="grid grid-cols-1 gap-3 rounded-2xl border border-farm-border bg-white p-3 sm:grid-cols-2">
        <div>{renderSortControls("yuklemeNo", "Küçükten büyüğe", "Büyükten küçüğe")}<FilterSearchInput value={filters.yuklemeNo} onChange={(value) => setFilters((prev) => ({ ...prev, yuklemeNo: value }))} placeholder="Yükleme No ara" /></div>
        <div>{renderSortControls("tarih", "Eskiden yeniye", "Yeniden eskiye")}<input type="date" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} className="mb-2 h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /><input type="date" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /></div>
        <div>{renderSortControls("fisNo")}<FilterSearchInput value={filters.fisNo} onChange={(value) => setFilters((prev) => ({ ...prev, fisNo: value }))} placeholder="Fiş No ara" /></div>
        <div>{renderSortControls("aciklama")}<FilterSearchInput value={filters.aciklama} onChange={(value) => setFilters((prev) => ({ ...prev, aciklama: value }))} placeholder="Açıklama ara" /></div>
        <div>{renderSortControls("tutar", "Küçükten büyüğe", "Büyükten küçüğe")}<FilterRangeInputs min={filters.tutar.min} max={filters.tutar.max} onMinChange={(value) => setFilters((prev) => ({ ...prev, tutar: { ...prev.tutar, min: value } }))} onMaxChange={(value) => setFilters((prev) => ({ ...prev, tutar: { ...prev.tutar, max: value } }))} /></div>
        <div className="sm:col-span-2">
          {renderSortControls("durum")}
          <FilterOptionList options={["Aktif", "Otomatik Dolduruldu", "Manuel Dolduruldu"]} selected={filters.durum} onToggle={(value) => setFilters((prev) => ({ ...prev, durum: toggleSelection(prev.durum, value) }))} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farm-cream px-3 py-4 font-body sm:px-5">
      <div className="mx-auto max-w-[1450px] space-y-4">
        <div className="lg:sticky lg:top-0 z-40 -mx-3 border-b border-farm-border/70 bg-farm-cream/95 px-3 pb-3 pt-1 shadow-sm backdrop-blur sm:-mx-5 sm:px-5">
        <div className="flex flex-col gap-3 rounded-farm border border-farm-border bg-white px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold text-farm-brown">Havale Ekranı</h1>
            <p className="mt-1 text-xs text-farm-muted">
              Örnek şablondaki Tarih, Fiş No, Açıklama ve Tutar alanlarını içeri alır. Aynı Fiş No ikinci kez eklenmez.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <BackButton />
            <a
              href={HAVALE_TEMPLATE_URL}
              download="havale-ornek-sablon.xlsx"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-farm-border bg-white px-4 text-sm font-semibold text-farm-brown hover:bg-farm-cream"
            >
              <i className="ri-download-2-line" />
              Örnek Şablon
            </a>
            <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full bg-farm-earth px-4 text-sm font-semibold text-white hover:bg-farm-brown">
              <i className="ri-file-excel-2-line" />
              {loading ? "Yükleniyor..." : "Excelden Yükle"}
              <input
                type="file"
                accept=".xlsx,.csv,.txt"
                disabled={loading}
                onChange={(event) => {
                  void handleFileUpload(event.target.files?.[0]);
                  event.currentTarget.value = "";
                }}
                className="hidden"
              />
            </label>
            <button
              type="button"
              onClick={() => setEditing(blankTransfer(maxYuklemeNo(transfers) + 1))}
              className="h-10 rounded-full border border-farm-border bg-white px-4 text-sm font-semibold text-farm-brown hover:bg-farm-cream"
            >
              Manuel Ekle
            </button>
          </div>
        </div>

        {message && <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{message}</div>}
        {error && <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-farm border border-farm-border bg-white p-3"><p className="text-xs text-farm-muted">Toplam Havale</p><p className="mt-1 text-lg font-bold text-farm-brown">{summary.total}</p></div>
          <div className="rounded-farm border border-farm-border bg-white p-3"><p className="text-xs text-farm-muted">Aktif</p><p className="mt-1 text-lg font-bold text-farm-green">{summary.active}</p></div>
          <div className="rounded-farm border border-farm-border bg-white p-3"><p className="text-xs text-farm-muted">Pasif</p><p className="mt-1 text-lg font-bold text-farm-muted">{summary.passive}</p></div>
          <div className="rounded-farm border border-farm-border bg-white p-3"><p className="text-xs text-farm-muted">Aktif Tutar</p><p className="mt-1 text-lg font-bold text-farm-earth">{fmtMoney(summary.activeAmount)}</p></div>
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-farm border border-farm-border bg-white p-3 md:grid-cols-3">
          <input
            value={filters.yuklemeNo}
            onChange={(event) => setFilters((prev) => ({ ...prev, yuklemeNo: event.target.value }))}
            placeholder="Yükleme No ara"
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
          <input
            value={filters.fisNo}
            onChange={(event) => setFilters((prev) => ({ ...prev, fisNo: event.target.value }))}
            placeholder="Fiş No ara"
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
          <input
            value={filters.aciklama}
            onChange={(event) => setFilters((prev) => ({ ...prev, aciklama: event.target.value }))}
            placeholder="Açıklama ara"
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
        </div>

        <div className="flex items-center justify-between gap-2 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            className="h-10 rounded-full border border-farm-border bg-white px-4 text-sm font-semibold text-farm-brown"
          >
            <i className="ri-filter-3-line mr-1" /> Mobil Filtreler
          </button>
          {hasActiveFilters && (
            <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="h-10 rounded-full border border-farm-border bg-white px-4 text-sm font-semibold text-farm-muted">Temizle</button>
          )}
        </div>
        {mobileFiltersOpen && <div className="lg:hidden">{renderFilterPanel()}</div>}

        <div className="overflow-visible rounded-t-farm border border-farm-border border-b-0 bg-farm-warm">
          <div className="overflow-visible">
            <div className="hidden min-w-[1360px] grid-cols-[95px_120px_150px_1fr_130px_180px_130px_120px_150px] border-b border-farm-border bg-farm-cream text-[10px] font-semibold uppercase tracking-wide text-farm-muted shadow-sm lg:grid">
              <HeaderFilterCell label="Yükleme No" active={Boolean(filters.yuklemeNo) || sortKey === "yuklemeNo"}>{renderSortControls("yuklemeNo", "Küçükten büyüğe", "Büyükten küçüğe")}<FilterSearchInput value={filters.yuklemeNo} onChange={(value) => setFilters((prev) => ({ ...prev, yuklemeNo: value }))} placeholder="No ara" /><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, yuklemeNo: "" }))} /></HeaderFilterCell>
              <HeaderFilterCell label="Tarih" active={Boolean(filters.dateFrom || filters.dateTo) || sortKey === "tarih"}>{renderSortControls("tarih", "Eskiden yeniye", "Yeniden eskiye")}<div className="space-y-2"><input type="date" value={filters.dateFrom} onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /><input type="date" value={filters.dateTo} onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /></div><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }))} hint="Aralık" /></HeaderFilterCell>
              <HeaderFilterCell label="Fiş No" active={Boolean(filters.fisNo) || sortKey === "fisNo"}>{renderSortControls("fisNo")}<FilterSearchInput value={filters.fisNo} onChange={(value) => setFilters((prev) => ({ ...prev, fisNo: value }))} placeholder="Fiş No ara" /><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, fisNo: "" }))} hint="Fiş No içinde arar" /></HeaderFilterCell>
              <HeaderFilterCell label="Açıklama" active={Boolean(filters.aciklama) || sortKey === "aciklama"}>{renderSortControls("aciklama")}<FilterSearchInput value={filters.aciklama} onChange={(value) => setFilters((prev) => ({ ...prev, aciklama: value }))} placeholder="Açıklama ara" /><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, aciklama: "" }))} /></HeaderFilterCell>
              <HeaderFilterCell label="Tutar" active={Boolean(filters.tutar.min || filters.tutar.max) || sortKey === "tutar"} align="right">{renderSortControls("tutar", "Küçükten büyüğe", "Büyükten küçüğe")}<FilterRangeInputs min={filters.tutar.min} max={filters.tutar.max} onMinChange={(value) => setFilters((prev) => ({ ...prev, tutar: { ...prev.tutar, min: value } }))} onMaxChange={(value) => setFilters((prev) => ({ ...prev, tutar: { ...prev.tutar, max: value } }))} /><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, tutar: { min: "", max: "" } }))} /></HeaderFilterCell>
              <div className="px-3 py-2">Manuel Açıklama</div>
              <div className="px-3 py-2 text-center">Otomatik Doldur</div>
              <div className="px-3 py-2 text-center">Manuel Doldur</div>
              <HeaderFilterCell label="Aktif / Pasif" active={filters.durum.length > 0 || sortKey === "durum"} align="right">{renderSortControls("durum")}<FilterOptionList options={["Aktif", "Otomatik Dolduruldu", "Manuel Dolduruldu"]} selected={filters.durum} onToggle={(value) => setFilters((prev) => ({ ...prev, durum: toggleSelection(prev.durum, value) }))} /><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, durum: [] }))} /></HeaderFilterCell>
            </div>
          </div>
        </div>
      </div>

        <div className="overflow-visible rounded-b-farm border border-farm-border border-t-0 bg-farm-warm">
          <div className="overflow-x-auto">
            <div className="divide-y divide-farm-border">
              {filtered.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-farm-muted">Havale kaydı bulunamadı.</div>
              ) : (
                filtered.map((item) => (
                  <div key={item.id} className="grid gap-1.5 px-3 py-2 text-xs lg:min-w-[1360px] lg:grid-cols-[95px_120px_150px_1fr_130px_180px_130px_120px_150px] lg:items-center">
                    <div className="font-bold text-farm-muted">#{getYuklemeNo(item) || "—"}</div>
                    <div className="font-semibold text-farm-brown">{formatDate(item.tarih)}</div>
                    <div className="font-bold text-farm-earth">{item.fisNo}</div>
                    <div className="min-w-0 text-farm-brown"><span className="line-clamp-1" title={item.aciklama}>{item.aciklama || "—"}</span></div>
                    <div className="font-bold text-farm-brown lg:text-right">{fmtMoney(item.tutar)}</div>
                    <div className="min-w-0">
                      <input
                        value={item.manualNote || ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          void setAllBankTransfers((prev) =>
                            prev.map((record) =>
                              record.id === item.id
                                ? { ...record, manualNote: value, updatedAt: new Date().toISOString() }
                                : record
                            )
                          );
                        }}
                        placeholder="İç not"
                        className="h-8 w-full rounded-lg border border-farm-border bg-white px-2 text-[11px] text-farm-brown outline-none focus:border-farm-earth"
                      />
                    </div>
                    <div className="text-center"><i className={item.automaticFilled ? "ri-checkbox-circle-fill text-base text-farm-green" : "ri-checkbox-blank-circle-line text-base text-farm-muted"} /></div>
                    <div className="text-center">
                      <button type="button" onClick={() => void toggleManual(item)} className="rounded-full p-1 text-farm-earth hover:bg-farm-cream" title="Manuel doldur/pasifleştir">
                        <i className={item.manualFilled ? "ri-checkbox-circle-fill text-base" : "ri-checkbox-blank-circle-line text-base text-farm-muted"} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${isBankTransferActive(item) ? "bg-farm-green-pale text-farm-green" : "bg-farm-border/60 text-farm-muted"}`}>{isBankTransferActive(item) ? "Aktif" : "Pasif"}</span>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setEditing(item)} className="h-7 w-7 rounded-full border border-farm-border text-farm-earth hover:bg-white" title="Düzenle"><i className="ri-edit-line" /></button>
                        <button type="button" onClick={() => void deleteTransfer(item.id)} className="h-7 w-7 rounded-full text-red-600 hover:bg-red-50" title="Sil"><i className="ri-delete-bin-line" /></button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-lg rounded-farm bg-white p-4 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-lg font-bold text-farm-brown">Havale Düzenle</h2>
                <p className="text-xs text-farm-muted">Fiş No benzersiz olmak zorunda.</p>
              </div>
              <button onClick={() => setEditing(null)} className="h-8 w-8 rounded-full text-farm-muted hover:bg-farm-cream"><i className="ri-close-line" /></button>
            </div>
            <div className="space-y-3">
              <input type="number" value={editing.yuklemeNo || ""} onChange={(e) => setEditing({ ...editing, yuklemeNo: Number(e.target.value || 0) || undefined })} placeholder="Yükleme No" className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none" />
              <input type="date" value={editing.tarih} onChange={(e) => setEditing({ ...editing, tarih: e.target.value })} className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none" />
              <input value={editing.fisNo} onChange={(e) => setEditing({ ...editing, fisNo: e.target.value })} placeholder="Fiş No" className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none" />
              <textarea value={editing.aciklama} onChange={(e) => setEditing({ ...editing, aciklama: e.target.value })} placeholder="Açıklama" rows={3} className="w-full rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-sm text-farm-brown outline-none" />
              <input type="number" value={editing.tutar || ""} onChange={(e) => setEditing({ ...editing, tutar: toWholeTl(e.target.value || 0) })} placeholder="Tutar" className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none" />
              <textarea value={editing.manualNote || ""} onChange={(e) => setEditing({ ...editing, manualNote: e.target.value })} placeholder="Manuel açıklama / iç not" rows={2} className="w-full rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-sm text-farm-brown outline-none" />
              <label className="flex items-center gap-2 rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-sm text-farm-brown">
                <input type="checkbox" checked={editing.manualFilled} onChange={(e) => setEditing({ ...editing, manualFilled: e.target.checked })} /> Manuel doldur / pasifleştir
              </label>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button onClick={() => setEditing(null)} className="h-10 flex-1 rounded-full border border-farm-border text-sm font-semibold text-farm-muted">Vazgeç</button>
              <button onClick={() => void saveEditing()} className="h-10 flex-1 rounded-full bg-farm-earth text-sm font-semibold text-white">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
